import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ShoppingCart, Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminOrders = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API_URL}/admin/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(response.data);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [token]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-700">Paid</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div data-testid="admin-orders">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-gray-900">Orders</h1>
        <p className="text-gray-600">View and manage customer orders</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-saffron-500" />
        </div>
      ) : orders.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No orders yet</h3>
            <p className="text-gray-500">Orders will appear here when customers make purchases</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <Card key={order.id} data-testid={`order-${order.id}`}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-sm text-gray-500">Order ID</p>
                    <p className="font-mono font-medium">{order.id.slice(0, 8)}...</p>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(order.status)}
                    <p className="text-sm text-gray-500 mt-1">{formatDate(order.created_at)}</p>
                  </div>
                </div>

                <div className="border-t border-b py-4 my-4 space-y-2">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span>{item.product_name} × {item.quantity}</span>
                      <span className="font-medium">${item.item_total.toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col gap-1">
                  {order.subtotal !== undefined && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span>${order.subtotal?.toFixed(2)}</span>
                    </div>
                  )}
                  {order.shipping_cost !== undefined && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Shipping</span>
                      <span>{order.free_shipping_applied ? 'FREE' : `$${order.shipping_cost?.toFixed(2)}`}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className="text-gray-600 font-medium">Total</span>
                    <span className="text-xl font-bold text-saffron-600">${order.total?.toFixed(2)}</span>
                  </div>
                </div>

                {order.shipping_address && (
                  <div className="text-sm text-gray-500 mt-4">
                    <span className="font-medium">Shipping to:</span>
                    {typeof order.shipping_address === 'object' ? (
                      <div className="mt-1 text-gray-600">
                        <p>{order.shipping_address.full_name}</p>
                        <p>{order.shipping_address.address_line1}</p>
                        {order.shipping_address.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                        <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}</p>
                        <p>{order.shipping_address.country}</p>
                        {order.shipping_address.phone && <p>Phone: {order.shipping_address.phone}</p>}
                      </div>
                    ) : (
                      <span> {order.shipping_address}</span>
                    )}
                  </div>
                )}

                {order.shipping_method_name && (
                  <div className="text-sm text-gray-500 mt-2">
                    <span className="font-medium">Shipping Method:</span> {order.shipping_method_name}
                    {order.shipping_cost !== undefined && (
                      <span> - ${order.shipping_cost.toFixed(2)}</span>
                    )}
                    {order.free_shipping_applied && (
                      <Badge className="ml-2 bg-green-100 text-green-700">FREE</Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
